import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
// import { SettingDrawer } from '@ant-design/pro-components';
import { App as AntdApp, ConfigProvider } from 'antd';
import React from 'react';
import avatar from '@/assets/avatar.png';
import {
  AvatarDropdown,
  AvatarName,
  NotificationBell,
  Question,
} from '@/components';
import HeaderTitle from '@/components/HeaderTitle';
import InitialStateBinder from '@/components/InitialStateBinder';
import MenuAccessGuard from '@/components/MenuAccessGuard';
import MenuLoadingGate from '@/components/MenuLoadingGate';
import type { AppearanceInfo } from '@/services/appearance';
import { getCloudStudioEntitlement } from '@/services/cloudStudio/entitlement';
import {
  type EntitlementSelf,
  getSelfEntitlement,
} from '@/services/inferencePro';
import request from '@/services/request';
import { getAuthUserInfo } from '@/services/user';
import {
  buildLoginUrl,
  isPublicAuthPath,
  persistTokensFromUrl,
} from '@/utils/authSession';
import {
  type CloudStudioActivation,
  filterCloudStudioMenu,
  isCloudStudioActivated,
} from '@/utils/cloudStudio';
import { filterMenuData } from '@/utils/layout';
import type { MenuNode, MenuSource, RawMenuItem } from '@/utils/menu';
import {
  fetchMenuDataBySource,
  findMenuNavigateTargetUnderMenu,
  findMenuNodeByMenuId,
  getMenuSource,
  isMenuGroupPath,
  normalizeMenuForProLayout,
  openBlankMenuUrls,
} from '@/utils/menu';
import {
  filterModelInferenceMenu,
  shouldShowModelInferenceConsoleMenu,
} from '@/utils/modelInferenceMenu';
import {
  type AppearanceSource,
  FIXED_APPEARANCE_SOURCE,
  getAppearanceFaviconUrl,
  getMenuSelectedBgColor,
  type RuntimeLayoutSettings,
  resolveAppearanceRuntime,
  setFavicon,
} from '@/utils/theme';
import defaultSettings from '../config/defaultSettings';

type InitialStateShape = {
  settings?: Partial<LayoutSettings>;
  currentUser?: USERS.AuthUserInfo;
  loading?: boolean;
  fetchUserInfo?: () => Promise<USERS.AuthUserInfo | undefined>;
  menuData?: MenuNode[];
  rawMenuData?: RawMenuItem[];
  menuSource?: MenuSource;
  appearanceSource?: AppearanceSource;
  menuDataLoaded?: boolean;
  menuLoading?: boolean;
  faviconPath?: string;
  themeColor?: string;
  systemName?: string;
  appearance?: AppearanceInfo;
  modelInferenceEntitlement?: EntitlementSelf | null;
  cloudStudioActivation?: CloudStudioActivation;
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<InitialStateShape> {
  const fetchUserInfo = async () => {
    try {
      const userInfo = await getAuthUserInfo({ skipErrorHandler: true });
      if (userInfo) {
        return userInfo;
      }
      return undefined;
    } catch (_error) {
      return undefined;
    }
  };
  // 如果不是登录页面，执行
  const { location } = history;

  if (typeof window !== 'undefined') {
    persistTokensFromUrl();
  }

  const {
    appearance,
    appearanceSource,
    runtimeSettings,
    faviconPath,
    themeColor,
    systemName,
  } = await resolveAppearanceRuntime({
    defaultSettings: defaultSettings as RuntimeLayoutSettings,
    source: FIXED_APPEARANCE_SOURCE,
  });
  const accessToken =
    typeof window !== 'undefined' ? localStorage.getItem('accessToken') : '';
  const hasToken = Boolean(accessToken);
  const currentUser = hasToken ? await fetchUserInfo() : undefined;
  const menuSource = getMenuSource();
  let menuData: MenuNode[] = [];
  let rawMenuData: RawMenuItem[] = [];
  let menuDataLoaded = false;
  /** 启动时预加载，避免进入模型推理时菜单结构才从扁平切到带子菜单导致选中字重闪烁 */
  let modelInferenceEntitlement: EntitlementSelf | null | undefined;
  let cloudStudioActivation: CloudStudioActivation | undefined;

  if (currentUser) {
    const [menuState, entitlement, cloudStudioEntitlement] = await Promise.all([
      fetchMenuDataBySource({
        currentUser,
        source: menuSource,
      }),
      getSelfEntitlement({ skipErrorHandler: true }),
      getCloudStudioEntitlement().catch(() => undefined),
    ]);
    menuData = menuState.menuData;
    rawMenuData = menuState.rawMenuData;
    menuDataLoaded = menuState.menuDataLoaded;
    modelInferenceEntitlement = entitlement;
    if (cloudStudioEntitlement !== undefined) {
      cloudStudioActivation = {
        userId: currentUser.userId,
        activated: Boolean(cloudStudioEntitlement?.entitlementId),
      };
    }
  }

  if (
    isPublicAuthPath(location.pathname) &&
    currentUser &&
    typeof window !== 'undefined'
  ) {
    history.replace('/');
  }

  const baseState: InitialStateShape = {
    fetchUserInfo,
    currentUser,
    settings: runtimeSettings,
    menuData,
    rawMenuData,
    menuSource,
    appearanceSource,
    menuDataLoaded,
    faviconPath,
    themeColor,
    systemName,
    appearance,
    modelInferenceEntitlement,
    cloudStudioActivation,
  };
  return baseState;
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  if (typeof window !== 'undefined') {
    const faviconHref = getAppearanceFaviconUrl(initialState?.appearance);
    setFavicon(faviconHref);
  }

  return {
    avatarProps: {
      src: avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    actionsRender: () => [
      <NotificationBell key="notification" />,
      <Question key="redeem-benefit" />,
    ],
    // 启用面包屑导航（即使菜单隐藏，面包屑仍然显示）
    breadcrumb: {},
    onPageChange: () => {
      const { location } = history;
      if (!initialState?.currentUser && !isPublicAuthPath(location.pathname)) {
        history.push(buildLoginUrl(`${location.pathname}${location.search}`));
        return;
      }
      // 菜单权限校验由 MenuAccessGuard 基于最新 @@initialState 处理
    },
    onMenuHeaderClick: () => {
      history.push('/');
    },
    headerTitleRender: () => <HeaderTitle />,
    // 使用动态菜单数据，并处理图标，同时过滤掉需要隐藏菜单的路径
    // 注意：即使菜单隐藏，也返回完整菜单数据，这样面包屑可以正常工作
    menuDataRender: () => {
      const menuData = initialState?.menuData || [];
      const showConsoleMenu = shouldShowModelInferenceConsoleMenu(
        initialState?.modelInferenceEntitlement,
        history.location.pathname,
      );
      const filtered = filterCloudStudioMenu(
        filterModelInferenceMenu(menuData, showConsoleMenu),
        isCloudStudioActivated(initialState),
      );
      return normalizeMenuForProLayout(filterMenuData(filtered)) as any;
    },
    menuItemRender: (item, dom) => {
      const menuItem = item as MenuNode & { children?: MenuNode[] };
      const rawMenuData = initialState?.menuData || [];
      if (menuItem.openMode === 'blank' && menuItem.externalUrl) {
        return (
          <button
            type="button"
            style={{
              display: 'block',
              width: '100%',
              padding: 0,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              textAlign: 'left',
            }}
            onClick={(event) => {
              event.preventDefault();
              window.open(
                String(menuItem.externalUrl),
                '_blank',
                'noopener,noreferrer',
              );
            }}
          >
            {dom}
          </button>
        );
      }
      if (menuItem.children?.length) {
        return dom;
      }
      const menuId = String(menuItem.menuId || menuItem.key || '').trim();
      if (isMenuGroupPath(menuItem.path) && menuId) {
        return (
          <button
            type="button"
            style={{
              display: 'block',
              width: '100%',
              padding: 0,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              textAlign: 'left',
            }}
            onClick={(event) => {
              event.preventDefault();
              const node = findMenuNodeByMenuId(rawMenuData, menuId);
              if (!node) {
                return;
              }
              const result = findMenuNavigateTargetUnderMenu(node);
              if (result.blankUrls.length) {
                openBlankMenuUrls(result.blankUrls);
              }
              if (result.path) {
                history.push(result.path);
              }
            }}
          >
            {dom}
          </button>
        );
      }
      if (
        !menuItem.path ||
        menuItem.path.startsWith('/external/') ||
        isMenuGroupPath(menuItem.path)
      ) {
        return dom;
      }
      return <Link to={menuItem.path}>{dom}</Link>;
    },
    childrenRender: (children) => {
      const colorPrimary =
        initialState?.themeColor ||
        initialState?.settings?.colorPrimary ||
        (defaultSettings.colorPrimary as string);
      const colorPrimaryBg = getMenuSelectedBgColor(colorPrimary);
      return (
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: colorPrimary as string,
              colorLink: colorPrimary as string,
              colorLinkHover: colorPrimary as string,
              colorPrimaryBg: colorPrimaryBg as string,
            },
          }}
        >
          <AntdApp>
            <InitialStateBinder />
            <MenuAccessGuard />
            <MenuLoadingGate>{children}</MenuLoadingGate>
          </AntdApp>
        </ConfigProvider>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name patchRoutes 动态修改路由（服务端和客户端都支持）
 * @description 根据权限动态生成路由
 */
export function patchRoutes({ routes }: { routes: any[] }) {
  // /iframe/:code 已在静态 routes 配置中声明，这里不做运行时改写，
  // 避免不同运行时传入的 routes 结构不一致导致异常。
  void routes;
}

/**
 * @name rootContainer 根容器
 * @description 包裹整个应用，用于动态应用主题色和 favicon
 */
export function rootContainer(container: React.ReactElement) {
  return container;
}

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export { request };
